/**
 * Frontend Logger with Firestore Persistence
 *
 * Creates a scoped logger that:
 * - debug: console only (DEV mode)
 * - info/warn: console only
 * - error: console + persists to Firestore `logs` collection via Cloud Function
 *
 * Rate-limiting prevents render-loop storms from flooding CF calls.
 * Max 1 identical error per module+message combo every 10 seconds.
 */

import { LogService } from '@shared/api';

interface Logger {
  debug: (message: string, ...args: unknown[]) => void;
  info: (message: string, ...args: unknown[]) => void;
  warn: (message: string, ...args: unknown[]) => void;
  error: (message: string, ...args: unknown[]) => void;
}

// ─── Rate-Limiting ────────────────────────────────────
const recentErrors = new Map<string, number>();
const RATE_LIMIT_MS = 10_000;

function shouldLog(module: string, message: string): boolean {
  const key = `${module}::${message}`;
  const now = Date.now();
  const lastSent = recentErrors.get(key) ?? 0;
  if (now - lastSent < RATE_LIMIT_MS) return false;
  recentErrors.set(key, now);
  // Cleanup: remove stale keys after 60s
  if (recentErrors.size > 100) {
    for (const [k, t] of recentErrors) {
      if (now - t > 60_000) recentErrors.delete(k);
    }
  }
  return true;
}

export function createLogger(module: string): Logger {
  const prefix = `[${module}]`;
  return {
    debug: (msg, ...args) => {
      if (import.meta.env.DEV) console.debug(prefix, msg, ...args);
    },
    info: (msg, ...args) => console.info(prefix, msg, ...args),
    warn: (msg, ...args) => console.warn(prefix, msg, ...args),
    error: (msg, ...args) => {
      console.error(prefix, msg, ...args);
      if (shouldLog(module, msg)) {
        const err = args.find((a) => a instanceof Error);
        const stack = err?.stack;
        const url = typeof window !== 'undefined' ? window.location.pathname : undefined;
        LogService.logError({
          module,
          message: msg,
          error: err ? `${err.name}: ${err.message}` : String(args[0] ?? ''),
          ...(stack ? { stack } : {}),
          ...(url ? { url } : {}),
        }).catch(() => {}); // fire-and-forget, no infinite loop
      }
    },
  };
}
